// Production-efficiency sequencing v1 (no RDD constraint yet):
// Material (PET -> ALUM) -> Water Type -> Bottle Size -> Cap Color -> Label Design (A-Z).
// Water/cap come from the design's structured associations (designMap), name regex as fallback.
// ALUM keeps its existing relative order; stable sort preserves ties.

const materialRank = (m) => (/alum/i.test(m.product_name || "") ? 2 : 1);

const waterRankOf = (s) => {
  if (/natural|spring/i.test(s)) return 1;
  if (/alkaline/i.test(s)) return 2;
  if (/electrolyte/i.test(s)) return 3;
  return 0;
};

const waterRank = (m, designMap) => {
  const structured = designMap?.[m.design_id]?.water || "";
  const r = waterRankOf(structured);
  if (r) return r;
  const fallback = waterRankOf(`${m.design_name || ""} ${m.product_name || ""}`);
  return fallback || 4;
};

const sizeRank = (m) => {
  const s = m.product_name || "";
  if (/12\s*oz|355\s*ml/i.test(s)) return 1;
  if (/16\.9\s*oz|500\s*ml/i.test(s)) return 2;
  if (/23\.7\s*oz|700\s*ml/i.test(s)) return 3;
  if (/33\.8\s*oz|1\s*l\b/i.test(s)) return 4;
  return 5;
};

const capRankOf = (t) => {
  if (/navy|blue/i.test(t)) return 1;
  if (/black/i.test(t)) return 2;
  if (/natural|clear/i.test(t)) return 3;
  if (/red/i.test(t)) return 4;
  return 0;
};

const capRank = (m, designMap) => {
  const structured = designMap?.[m.design_id]?.cap || "";
  const r = capRankOf(structured);
  if (r) return r;
  const bomCap = (m.bom_snapshot || []).find((b) => /cap/i.test(b.name || ""));
  return capRankOf(bomCap?.type || "") || 5;
};

// Build designId -> { water, cap } from the products list (design associations).
export function buildDesignMap(products) {
  const map = {};
  (products || []).forEach((p) => (p.lines || []).forEach((ln) => (ln.designs || []).forEach((d) => {
    if (!d?.id || map[d.id]) return;
    const assoc = d.associations || [];
    const find = (re) => assoc.find((a) => re.test(a.component_name || ""))?.variant_name || "";
    map[d.id] = {
      water: d.additive_name || find(/additive/i) || d.water_name || find(/water/i) || "",
      cap: d.cap_name || find(/cap/i) || "",
    };
  })));
  return map;
}

export function productionSequenceCompare(a, b, designMap) {
  const mat = materialRank(a) - materialRank(b);
  if (mat !== 0) return mat;
  if (materialRank(a) === 2) return 0; // ALUM: preserve existing relative order
  const w = waterRank(a, designMap) - waterRank(b, designMap);
  if (w !== 0) return w;
  const s = sizeRank(a) - sizeRank(b);
  if (s !== 0) return s;
  const c = capRank(a, designMap) - capRank(b, designMap);
  if (c !== 0) return c;
  return (a.design_name || "").localeCompare(b.design_name || "");
}

export const moWater = (m, designMap) => {
  const s = designMap?.[m.design_id]?.water || "";
  if (s) return /electrolyte/i.test(s) ? "Electrolyte" : /alkaline/i.test(s) ? "Alkaline" : /natural|spring/i.test(s) ? "Natural Spring" : s;
  const n = `${m.design_name || ""} ${m.product_name || ""}`;
  if (/natural\s*spring/i.test(n)) return "Natural Spring";
  if (/alkaline/i.test(n)) return "Alkaline";
  if (/electrolyte/i.test(n)) return "Electrolyte";
  return "";
};

export const moCap = (m, designMap) =>
  designMap?.[m.design_id]?.cap || (m.bom_snapshot || []).find((b) => /cap/i.test(b.name || ""))?.type || "";

const permutations = (arr) => (arr.length <= 1 ? [arr] : arr.flatMap((v, i) => permutations([...arr.slice(0, i), ...arr.slice(i + 1)]).map((p) => [v, ...p])));

// 12 oz (1) and 16.9 oz (2) share the same blow mold — never run them back to back.
const sameMold = (a, b) => (a === 1 && b === 2) || (a === 2 && b === 1);

const outstanding = (m) => Math.max(0, (Number(m.ordered_bottles) || Number(m.bottle_quantity) || 0) - (Number(m.produced_bottles) || 0));

export function sequenceMos(mos, designMap) {
  const sorted = [...mos].sort((a, b) => productionSequenceCompare(a, b, designMap));
  const pet = sorted.filter((m) => !/alum/i.test(m.product_name || ""));
  const alum = sorted.filter((m) => /alum/i.test(m.product_name || ""));
  // Group PET by water type, then contiguous size groups (already sorted internally by cap/label).
  const waterGroups = [];
  pet.forEach((m) => {
    const w = waterRank(m, designMap);
    let wg = waterGroups[waterGroups.length - 1];
    if (!wg || wg.water !== w) { wg = { water: w, sizes: [] }; waterGroups.push(wg); }
    const s = sizeRank(m);
    let sg = wg.sizes[wg.sizes.length - 1];
    if (!sg || sg.size !== s) { sg = { size: s, demand: 0, items: [] }; wg.sizes.push(sg); }
    sg.items.push(m);
    sg.demand += outstanding(m);
  });
  // Within each water type: highest total outstanding demand first, but the 12/16.9 same-mold
  // constraint (including the boundary with the previous water type) overrides demand ranking.
  let prevEnd = null;
  const out = [];
  waterGroups.forEach((wg) => {
    let best = wg.sizes, bestScore = Infinity;
    permutations(wg.sizes).forEach((perm) => {
      let score = 0;
      for (let i = 1; i < perm.length; i++) if (sameMold(perm[i - 1].size, perm[i].size)) score += 1000;
      if (prevEnd != null && perm.length && sameMold(prevEnd, perm[0].size)) score += 1000;
      for (let i = 0; i < perm.length; i++) for (let j = i + 1; j < perm.length; j++) if (perm[i].demand < perm[j].demand) score += 1;
      if (score < bestScore) { bestScore = score; best = perm; }
    });
    best.forEach((sg) => out.push(...sg.items));
    if (best.length) prevEnd = best[best.length - 1].size;
  });
  return [...out, ...alum];
}

const addDaysIso = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

// Latest Production Date = requested ship date (production_date) − 1 day.
const lpdOf = (m) => (m.production_date ? addDaysIso(m.production_date, -1) : null);

// RDD-constrained mode: start from the efficient sequence, then pull orders forward only as much
// as necessary (earliest deadline first), assuming one production day per MO starting today.
// Returns { list, riskIds } — riskIds = MOs that cannot meet their Latest Production Date at all.
export function sequenceWithRdd(mos, designMap, todayIso) {
  const remaining = sequenceMos(mos, designMap);
  const list = [];
  const riskIds = new Set();
  const n = remaining.length;
  for (let pos = 0; pos < n; pos++) {
    const day = addDaysIso(todayIso, pos);
    let pick = null, urgentLpd = null;
    remaining.forEach((m) => {
      const l = lpdOf(m);
      if (l && l <= day && (urgentLpd == null || l < urgentLpd)) { urgentLpd = l; pick = m; }
    });
    if (!pick) pick = remaining[0];
    remaining.splice(remaining.indexOf(pick), 1);
    const l = lpdOf(pick);
    if (l && day > l) riskIds.add(pick.id);
    list.push(pick);
  }
  return { list, riskIds };
}
